import classNames from 'classnames';
import styled from 'styled-components';
import { PropsWithClassName } from './PropsWithClassName';
import { ReactElement, useCallback } from 'react';
import { Icon, IconName } from '../icon';
import { Tooltip } from '../tooltip';

const TabButtonRoot = styled.div`
  font-family: ${props => props.theme.font};
  display: inline-block;
  cursor: pointer;
  width: auto;
  text-align: center;
  font-weight: 400;
  white-space: nowrap;
  flex: 1 auto;
  color: ${props => props.theme.tabs.default.color};
  min-height: 25px;
  user-select: none;
  max-width: 100%;

  &:after {
    content: '';
    display: block;
    width: 0;
    height: 2px;
    background: ${props => props.theme.tabs.hoverHighlight};
    transition: width .2s;
    animation-timing-function: ease;
  }

  &:hover {
    color: ${props => props.theme.tabs.hover.color};
    &:after {
      width: 100%;
    }

    ${Icon.cssSelector} {
      color: ${props => props.theme.tabs.hover.icon};
    }
  }

  &.tab-button--selected {
    color: ${props => props.theme.tabs.active.color};

    ${Icon.cssSelector} {
      color: ${props => props.theme.tabs.active.icon};
    }

    &:after {
      width: 100%;
      background: ${props => props.theme.tabs.highlight};
    }
  }
  &.tab-button--disabled {
    pointer-events: none;
    cursor: not-allowed;
    opacity: 0.5;
  }

  &.tab-button--error {

    &:after {
      width: 100%;
      background: ${props => props.theme.tabs.error.color};
    }
  }
`;

const TabButtonContent = styled.div`
  height: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
`;

const TabIcon = styled(Icon)`
  color: ${props => props.theme.tabs.default.icon};
  height: 24px;
  width: 24px;
  font-size: 18px;
`;

const TabLabel = styled.div`
	padding: 0 10px;

	// The content's gap already spaces an icon from its label; the padding on
	// top of it pushed the two 16px apart.
	${TabIcon} + & {
		padding-left: 0;
	}
`;

type TabButtonProps = PropsWithClassName<{
    id: string;
    label: string;
	chip?: ReactElement;
    icon?: IconName;
    selected?: boolean;
    disabled?: boolean;
    error?: boolean;
    message?: string;
    onSelect: (id: string) => void;
}>;

export const TabButton = ({
	id, className, chip, label, icon, message, selected, disabled, error = false, onSelect
}: TabButtonProps) => {
	const handleClick = useCallback(() => onSelect(id), [id, onSelect]);

	return <Tooltip message={message} placement={'top'}>
		<span>
			<TabButtonRoot
				className={classNames('tab-button', className, {
					'tab-button--selected': selected,
					'tab-button--disabled': disabled,
					'tab-button--error': error,
				})}
				onClick={handleClick}
			>
				<TabButtonContent>
					{chip}
					{icon && <TabIcon icon={icon} />}
					<TabLabel>{label}</TabLabel>
				</TabButtonContent>
			</TabButtonRoot>
		</span>

	</Tooltip>;
};
